import { FormApi } from "final-form";
import _ from "lodash";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";
import { useFetchVisibleProductsQuery } from "src/ui/containers/dashboard/products/api";
import { Modal } from "src/ui/shared/Modal/Modal";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";

import { createDevice } from "@dashboard/devices/store";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box, Typography } from "@mui/material";
import { PrimaryButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { TextField } from "@shared/form/TextField";
import { LoadingBox } from "@shared/Loading";
import { TextLink } from "@shared/TextLink";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";

import { useStyles } from "./AddDeviceModal.styles";
import {
    macAddressPattern,
    makeSchema,
    normalizeMacAddress,
    parseQrCodeValue,
    resolveProduct,
    serialSchema,
    useDevices,
} from "./AddDeviceModal.utils";
import { AddedDevice, AddedDeviceType } from "./AddedDevice";
import { QrScanner } from "./QrScanner.lazy";

export type FormValues = {
    androidSerial: string;
    macAddress?: string;
    productName: string;
    groupId: number;
};

type EnrollmentRequest = {
    androidSerial: string;
    productName: string;
    groupId: number;
    mac?: string;
};

type Props = {
    open: boolean;
    onClose: () => void;
};

export const AddDeviceModal = (props: Props) => {
    const { addedDevices, setAddedDevices, devices } = useDevices();
    const [mode, setMode] = React.useState<"scan" | "manual">("scan");
    // The scanner forgets seen codes when it remounts (after "Back to scanning"), so a label still under the camera would be handled again.
    const lastScanned = React.useRef<string | null>(null);

    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);
    const dispatch = useDispatch();

    const visibleProductsResult = useFetchVisibleProductsQuery(undefined, { skip: !props.open });
    const { data: visibleProducts = [], isLoading } = visibleProductsResult;
    useQueryErrorToast(visibleProductsResult);
    const productOptions = _.orderBy(
        visibleProducts.map((product) => ({
            label: product.productName,
            value: product.codename,
        })),
        "label",
    );

    const groups = useSelector(selectGroups);
    const groupOptions = _.orderBy(
        groups.map((group) => ({
            label: group.name,
            value: group.id,
        })),
        "label",
    );
    const { data: workspace } = useCurrentWorkspace();
    const defaultGroupId = groupOptions.find((option) => option.value === workspace?.defaultGroupId)?.value;

    const isEnrolled = React.useCallback(
        (serial: string) => devices.current.some((device) => device.serial === serial),
        [devices],
    );
    const validate = useValidationSchema(React.useMemo(() => makeSchema(isEnrolled), [isEnrolled]));

    const enroll = async (request: EnrollmentRequest): Promise<AddedDeviceType> => {
        const productName =
            visibleProducts.find((product) => product.codename === request.productName)?.productName ?? request.productName;
        const device = { androidSerial: request.androidSerial, productName, groupId: request.groupId };
        try {
            await dispatch(createDevice(request)).unwrap();
            return { ...device, error: null, createdAt: new Date().toISOString() };
        } catch (error) {
            const err = error as Error;
            const message = err.message || t("modals:addDevice.toast.deviceCreationFailed");
            return { ...device, error: message, createdAt: new Date().toISOString() };
        }
    };
    const report = (device: AddedDeviceType, errorToast = device.error) => {
        setAddedDevices([device]);
        if (errorToast) {
            showErrorToast(errorToast);
        } else {
            showSuccessToast(t("modals:addDevice.toast.deviceCreatedSuccess"));
        }
    };

    const onScan = async (rawValue: string, form: FormApi<FormValues>) => {
        if (rawValue === lastScanned.current) {
            return;
        }
        const content = parseQrCodeValue(rawValue);
        const groupId = form.getState().values.groupId;
        if (groupId === undefined) {
            showErrorToast(t("modals:addDevice.scanError.groupMissing"));
            return;
        }
        const reject = (error: string, errorToast?: string) =>
            report(
                {
                    androidSerial: content.serial,
                    productName: content.model ?? "",
                    groupId,
                    error,
                    createdAt: new Date().toISOString(),
                },
                errorToast,
            );

        const serialResult = serialSchema.safeParse(content.serial);
        if (!serialResult.success) {
            reject(serialResult.error.issues[0].message);
            return;
        }
        if (isEnrolled(content.serial)) {
            reject(t("modals:addDevice.toast.deviceAlreadyExists"));
            return;
        }
        const switchToManual = (productName?: string) => {
            // The prefill has to become the initial values: react-final-form sets a newly mounted field to its initial value.
            form.reset({
                ...form.getState().values,
                androidSerial: content.serial,
                macAddress: content.mac ?? "",
                productName,
            });
            lastScanned.current = rawValue;
            setMode("manual");
        };

        const product = resolveProduct(content.model, visibleProducts);
        if ("error" in product) {
            reject(product.error, product.errorToast);
            switchToManual();
            return;
        }
        if (content.mac && !macAddressPattern.test(content.mac)) {
            reject(t("modals:addDevice.scanError.macInvalid"), t("modals:addDevice.scanError.macInvalidEnrollManually"));
            switchToManual(product.codename);
            return;
        }

        const device = await enroll({
            androidSerial: content.serial,
            productName: product.codename,
            groupId,
            mac: normalizeMacAddress(content.mac),
        });
        report(device);
        if (!device.error) {
            lastScanned.current = rawValue;
        }
    };
    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        const device = await enroll({
            androidSerial: values.androidSerial.trim(),
            productName: values.productName,
            groupId: values.groupId,
            mac: normalizeMacAddress(values.macAddress),
        });
        report(device);
        if (!device.error) {
            form.restart({ ...values, androidSerial: "", macAddress: "" });
        }
    };
    const onClose = (form: FormApi<FormValues>) => {
        lastScanned.current = null;
        setAddedDevices([]);
        setMode("scan");
        form.restart({ androidSerial: "", macAddress: "", groupId: defaultGroupId });
        props.onClose();
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            validateOnBlur={true}
            initialValues={{
                androidSerial: "",
                macAddress: "",
                groupId: defaultGroupId,
            }}
            render={({ handleSubmit, form, submitting, values }) => (
                <Modal
                    maxWidth="md"
                    fullWidth
                    open={props.open}
                    onClose={() => onClose(form)}
                    title={t("modals:addDevice.title")}
                    description={t("modals:addDevice.description")}
                >
                    {isLoading ? (
                        <Box className={classes.loadingBox}>
                            <LoadingBox />
                        </Box>
                    ) : (
                        <Box className={classes.modalContent}>
                            <form onSubmit={handleSubmit} className={classes.form}>
                                <ControlledSelectField
                                    name="groupId"
                                    label={t("modals:addDevice.input.group.caption")}
                                    options={groupOptions}
                                    searchable
                                />
                                {mode === "scan" ? (
                                    <>
                                        <QrScanner
                                            onScan={(rawValue) => onScan(rawValue, form)}
                                            onLoadError={() => setMode("manual")}
                                        />
                                        <Typography variant="body2" component="div" className={classes.modeSwitch}>
                                            <TextLink
                                                component="button"
                                                type="button"
                                                tooltip={t("modals:addDevice.enterManuallyTooltip")}
                                                onClick={() => setMode("manual")}
                                            >
                                                {t("modals:addDevice.enterManually")}
                                            </TextLink>
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Box className={classes.manualFields}>
                                            <ControlledSelectField
                                                name="productName"
                                                label={t("common:label.product")}
                                                options={productOptions}
                                                searchable
                                            />
                                            <TextField
                                                name="androidSerial"
                                                label={t("modals:addDevice.input.androidSerial.caption")}
                                            />
                                            <TextField name="macAddress" label={t("modals:addDevice.input.macAddress.caption")} />
                                            <Box className={classes.formButtonContainer}>
                                                <PrimaryButton
                                                    onClick={form.submit}
                                                    disabled={
                                                        submitting ||
                                                        !values.androidSerial?.trim() ||
                                                        !values.productName ||
                                                        values.groupId === undefined
                                                    }
                                                    loading={submitting}
                                                >
                                                    {t("common:button.enroll")}
                                                </PrimaryButton>
                                            </Box>
                                        </Box>
                                        <Typography variant="body2" component="div" className={classes.modeSwitch}>
                                            <TextLink
                                                component="button"
                                                type="button"
                                                tooltip={t("modals:addDevice.backToScanningTooltip")}
                                                onClick={() => setMode("scan")}
                                            >
                                                <i className={`fa-solid fa-arrow-left ${classes.backIcon}`} />
                                                {t("modals:addDevice.backToScanning")}
                                            </TextLink>
                                        </Typography>
                                    </>
                                )}
                            </form>
                            <Box className={classes.devicesList}>
                                {addedDevices.length ? (
                                    addedDevices.map((device) => (
                                        <AddedDevice key={`${device.androidSerial}-${device.createdAt}`} {...device} />
                                    ))
                                ) : (
                                    <Box className={classes.emptyDevicesList}>
                                        <Typography>{t("modals:addDevice.noDevices")}</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Modal>
            )}
        />
    );
};
